import { MouseEvent } from 'react';
import { DocumentLinks } from '../components/DocumentLinks';
import { useSiteContent } from '../hooks/useSiteContent';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { mediaUrl } from '../services/api';
import { DocumentItem } from '../types/content';
import { downloadDoc, getDocDisplayTitle, getDocFileName, normalizeDocText } from '../utils/documents';
import { CertificateDomeIcon, CertificateTheme } from '../components/CertificateDomeIcon';
import aboutShowcaseImage from '../components/assets/11.jpg';
import certificatesBackground from '../components/assets/kapak_home.jpg';
import iconEye from '../components/assets/icon-eye.png';
import iconTarget from '../components/assets/icon-target.png';

const PAGE_SLUG = 'hakkimizda';

const CERTIFICATE_SPECS: {
  key: string;
  title: string;
  standard: string;
  subtitle: string;
  theme: CertificateTheme;
}[] = [
  {
    key: 'iso 9001',
    title: 'ISO 9001',
    standard: 'ISO 9001:2015',
    subtitle: 'Kalite Yönetimi',
    theme: 'red',
  },
  {
    key: 'iso 14001',
    title: 'ISO 14001',
    standard: 'ISO 14001:2015',
    subtitle: 'Çevre Yönetimi',
    theme: 'green',
  },
  {
    key: 'iso 45001',
    title: 'ISO 45001',
    standard: 'ISO 45001:2018',
    subtitle: 'İş Sağlığı ve Güvenliği',
    theme: 'yellow',
  },
  {
    key: 'iso 50001',
    title: 'ISO 50001',
    standard: 'ISO 50001:2018',
    subtitle: 'Enerji Yönetimi',
    theme: 'gray',
  },
];

const IMAGE_FILE = /\.(png|jpe?g|webp|gif)(\?.*)?$/i;

const DEFAULTS = {
  heroEyebrow: 'HAKKIMIZDA',
  heroTitle: "Adana'nın <em>Dünyaya</em> Açılan Kapısı",
  heroDescription:
    "Adana'da 1.300.000 m² toplam alanda, SANKO Holding güvencesiyle ticarete kesintisiz akış sağlıyoruz. Şeffaf, güvenilir ve sürdürülebilir hizmet anlayışımızla bölgemizin küresel ticaretteki yerini büyütüyoruz.",
  corporateEyebrow: 'BİZ KİMİZ',
  corporateTitle: "Doğu Akdeniz'in <span>stratejik ticaret üssü</span>",
  corporateDescription:
    "TBH Port Adana çok amaçlı bir liman işletmesidir. Konteyner, genel kargo, dökme yük ve proje yüklere hizmet vermektedir. 1.300.000 m² genişliğindeki terminal alanı ile özel olarak tasarlanmış depo ve siloları, gelecekteki büyüme ve yatırımlar için lojistik çözüm önerileri sunmaktadır.",
  valuesEyebrow: 'VİZYON & MİSYON',
  valuesTitle: '<span>Geleceği</span> bugünden inşa ediyoruz',
  valuesDescription:
    'Şeffaflık üzerine kurulu ticaret anlayışımız, her kararımızda ve her adımımızda yol göstericimizdir.',
  vision: {
    title: 'Vizyonumuz',
    content: "Doğu Akdeniz'in en güvenilir ve sürdürülebilir liman operatörü olmak.",
  },
  mission: {
    title: 'Misyonumuz',
    content:
      'Müşterilerimize güvenli, hızlı ve şeffaf liman hizmetleri sunarak bölgesel ticaretin gelişimine katkıda bulunmak.',
  },
  values: {
    title: 'Değerlerimiz',
    content: 'Dürüstlük, güvenilirlik, şeffaflık ve sürdürülebilirlik tüm operasyonlarımızın temelidir.',
  },
};

function normalizeLookup(value?: string) {
  return (value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatAboutHeroTitle(value?: string) {
  const text = value?.trim();
  if (!text) return '';
  if (text.includes('<')) return text;

  const parts = text.split(/\s+/);
  if (parts.length < 2) return text;
  const [first, ...rest] = parts;
  return `<em>${first}</em> ${rest.join(' ')}`;
}

// Ilk iki kelime ilk satirda lacivert, ucuncu kelime kirmizi, kalani lacivert.
function formatCorporateTitle(value?: string) {
  const text = value?.trim();
  if (!text) return '';
  if (text.includes('<')) return text;

  const parts = text.split(/\s+/);
  if (parts.length < 3) return text;

  const firstLine = parts.slice(0, 2).join(' ');
  const accent = parts[2];
  const rest = parts.slice(3).join(' ');
  return `${firstLine}<br /><span>${accent}</span>${rest ? ` ${rest}` : ''}`;
}

export function AboutPage() {
  const { getPage, getBlock, getStats, content } = useSiteContent();
  const { t } = useLanguage();
  const page = getPage(PAGE_SLUG);
  const findAboutBlock = (...patterns: string[]) =>
    content.contentBlocks.find((block) => {
      if (block.pageSlug !== PAGE_SLUG) return false;
      const haystack = normalizeLookup(`${block.title || ''} ${block.content || ''} ${block.blockKey || ''}`);
      return patterns.some((pattern) => haystack.includes(normalizeLookup(pattern)));
    });

  const heroEyebrow =
    getBlock(PAGE_SLUG, 'about_hero_eyebrow') ||
    getBlock(PAGE_SLUG, 'about_banner_eyebrow');
  const heroTitle =
    getBlock(PAGE_SLUG, 'about_hero_title') ||
    getBlock(PAGE_SLUG, 'about_banner_title');
  const heroDescription =
    getBlock(PAGE_SLUG, 'about_hero_description') ||
    getBlock(PAGE_SLUG, 'about_banner_description');

  const corporateEyebrow =
    getBlock(PAGE_SLUG, 'about_corporate_eyebrow') ||
    findAboutBlock('Kurumsal - Ust Etiket');
  const corporateTitle =
    getBlock(PAGE_SLUG, 'about_corporate_title') ||
    findAboutBlock('Kurumsal - Baslik');
  const corporateDescription =
    getBlock(PAGE_SLUG, 'about_corporate_description') ||
    findAboutBlock('Kurumsal - Aciklama');
  const corporateImage =
    getBlock(PAGE_SLUG, 'about_corporate_image') ||
    findAboutBlock('Kurumsal - Gorsel URL');

  const valuesEyebrow = getBlock(PAGE_SLUG, 'about_values_eyebrow');
  const valuesTitle = getBlock(PAGE_SLUG, 'about_values_title');
  const valuesDescription = getBlock(PAGE_SLUG, 'about_values_description');
  const vision = getBlock(PAGE_SLUG, 'vision');
  const mission = getBlock(PAGE_SLUG, 'mission');
  const values = getBlock(PAGE_SLUG, 'values');

  const valueCards = [
    {
      key: 'vision',
      title: vision?.title || DEFAULTS.vision.title,
      content: vision?.content || DEFAULTS.vision.content,
      documents: vision?.documents,
      icon: iconEye,
    },
    {
      key: 'mission',
      title: mission?.title || DEFAULTS.mission.title,
      content: mission?.content || DEFAULTS.mission.content,
      documents: mission?.documents,
      icon: iconTarget,
    },
  ];

  const heroStats = getStats('hero');
  const heroShowcaseStats = [
    heroStats.find((item) => item.value.toLocaleLowerCase('tr-TR').includes('teu')),
    heroStats.find((item) => item.value.toLocaleLowerCase('tr-TR').includes('m²')),
  ].filter((item): item is NonNullable<typeof item> => Boolean(item));

  const heroImage = mediaUrl(page?.heroImage);
  const showcaseImage =
    mediaUrl(corporateImage?.imageUrl || corporateImage?.content) || aboutShowcaseImage;
  const capacityBadge =
    heroShowcaseStats[0]
      ? `${heroShowcaseStats[0].value} ${heroShowcaseStats[0].label}`
      : '250K TEU Yıllık Konteyner Kapasitesi';

  const certificates = CERTIFICATE_SPECS.map((spec) => ({
    ...spec,
    // "ISO 9001" toLocaleLowerCase('tr-TR') ile "ıso 9001" olur; normalizeDocText i'ye cevirir.
    // Dosya adlarindaki alt tire de bosluk sayilir ("ISO_9001 1.pdf").
    // Portalda yonetilen kartlarda ISO ikonu secilmis kart (orn. iso9001) once aranir
    document:
      content.documents?.find((doc) => doc.iconKey === spec.key.replace(/\s+/g, '')) ||
      content.documents?.find((doc) =>
        normalizeDocText(doc.title).replace(/[_-]+/g, ' ').includes(spec.key),
      ),
  }));

  return (
    <>
      <section
        className="about-hero-section"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="about-hero-overlay" />
        <div className="container about-hero-inner">
          <div className="eyebrow about-hero-eyebrow">
            {t(heroEyebrow?.content || heroEyebrow?.title || page?.title || DEFAULTS.heroEyebrow)}
          </div>
          <h1
            className="about-hero-title"
            dangerouslySetInnerHTML={{
              __html: formatAboutHeroTitle(
                t(heroTitle?.content || heroTitle?.title || page?.heroTitle || ''),
              ),
            }}
          />
          <p className="about-hero-description">
            {t(heroDescription?.content || heroDescription?.title || page?.heroSubtitle || '')}
          </p>
          <div className="about-hero-stats">
            {heroShowcaseStats.map((item) => (
              <div key={item.id}>
                <b>{item.value}</b>
                <span>{t(item.label)}</span>
              </div>
            ))}
          </div>
          <Link to="/iletisim" className="btn btn-red about-hero-button">
            {t('TEKLİF AL')}
          </Link>
        </div>
      </section>

      <section className="about-intro-section">
        <div className="container about-intro-layout">
          <div className="about-intro-image-wrap">
            {showcaseImage && (
              <img
                src={showcaseImage}
                alt={t(corporateTitle?.content || corporateTitle?.title || 'TBH Port Adana')}
                className="about-intro-image"
              />
            )}
            <div className="about-intro-badge">{t(capacityBadge)}</div>
          </div>
          <div className="about-intro-copy">
            <div className="about-intro-eyebrow">
              {t(corporateEyebrow?.content || DEFAULTS.corporateEyebrow)}
            </div>
            <h2
              className="about-intro-title"
              dangerouslySetInnerHTML={{
                __html: formatCorporateTitle(
                  t(corporateTitle?.content || corporateTitle?.title || DEFAULTS.corporateTitle),
                ),
              }}
            />
            {/* Portalda bos satirla ayrilan paragraflar ayri gosterilir; paragraf icindeki
                tekli satir sonlari bosluk sayilir */}
            {(corporateDescription?.content || DEFAULTS.corporateDescription)
              .replace(/\\n/g, '\n')
              .split(/\r?\n\s*\r?\n/)
              .map((paragraph) => paragraph.replace(/\s*\r?\n\s*/g, ' ').trim())
              .filter(Boolean)
              .map((paragraph, index) => (
                <p className="about-intro-text" key={`corporate-${index}`}>
                  {t(paragraph)}
                </p>
              ))}
            <DocumentLinks
              documents={[
                ...(corporateDescription?.documents || []),
                ...(corporateImage?.documents || []),
              ]}
            />
          </div>
        </div>
      </section>

      <section className="about-bottom-section">
        <div className="about-bottom-cta">
          <div className="container about-bottom-cta-inner">
            <h3>{t('Ticaretinizin güvenilir limanı: TBH Port Adana')}</h3>
            <Link to="/iletisim" className="about-bottom-cta-button">
              {t('TEKLİF AL')}
            </Link>
          </div>
        </div>

        <div className="container about-values-shell">
          <div className="about-values-eyebrow">
            {t(valuesEyebrow?.content || DEFAULTS.valuesEyebrow)}
          </div>
          <h2
            className="about-values-title"
            dangerouslySetInnerHTML={{
              __html: t(valuesTitle?.content || DEFAULTS.valuesTitle),
            }}
          />
          <p className="about-values-description">
            {t(valuesDescription?.content || DEFAULTS.valuesDescription)}
          </p>

          <div className="about-values-grid">
            {valueCards.map((card) => (
              <div className="about-value-card" key={card.key}>
                <div className="about-value-card-icon">
                  <img src={card.icon} alt="" />
                </div>
                <h3>{t(card.title)}</h3>
                <p>{t(card.content)}</p>
                <DocumentLinks documents={card.documents} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-certificates-section">
        <div className="container">
          <div className="about-certificates-eyebrow">{t('SERTİFİKALARIMIZ')}</div>
          <h2
            className="about-certificates-title"
            dangerouslySetInnerHTML={{
              __html: t('<span>Küresel</span> standartlar - yerel sorumluluk'),
            }}
          />
          <p className="about-certificates-description">
            {t(
              'Entegre Yönetim Sistemimiz, dört temel disiplinde uluslararası belgelendirmeyle taçlandırılmıştır.',
            )}
          </p>

          <div className="about-certificates-shell">
            <div
              className="about-certificates-grid"
              style={{ backgroundImage: `url(${certificatesBackground})` }}
            >
              {certificates.map((item) => {
                // Portala sertifikanin gorseli yuklendiyse dogrudan o gosterilir;
                // PDF ise ayni tasarimda bir onizleme cizilir.
                const certificateImage =
                  item.document &&
                  (IMAGE_FILE.test(item.document.fileName || '') ||
                    IMAGE_FILE.test(item.document.fileUrl || ''))
                    ? mediaUrl(item.document.fileUrl)
                    : undefined;
                return (
                  <a
                    key={item.key}
                    className={`about-certificate-card ${item.theme}`}
                    href={item.document?.fileUrl || '#'}
                    title={item.document ? t(getDocDisplayTitle(item.document)) : undefined}
                    {...(item.document
                      ? {
                          download: getDocFileName(item.document),
                          onClick: (event: MouseEvent<HTMLAnchorElement>) =>
                            downloadDoc(event, item.document as DocumentItem),
                        }
                      : {})}
                  >
                    <div className="about-certificate-icon">
                      <CertificateDomeIcon theme={item.theme} />
                    </div>
                    <h3>{item.title}</h3>
                    <div className="about-certificate-line" />
                    <p>{t(item.subtitle)}</p>
                    {certificateImage ? (
                      <img className="about-certificate-photo" src={certificateImage} alt={item.standard} />
                    ) : (
                      <div className="about-certificate-preview">
                        <div className="about-certificate-preview-ribbon">SQR CERTIFICATION</div>
                        <div className="about-certificate-preview-sheet">
                          <div className="about-certificate-seal">SQR</div>
                          <div className="about-certificate-script">Sertifika</div>
                          <div className="about-certificate-script-sub">CERTIFICATE</div>
                          <div className="about-certificate-company">
                            SANKO PETROKİMYA MAMÜLLERİ
                            <br />
                            SANAYİ VE TİCARET A.Ş.
                            <br />
                            YUMURTALIK ŞUBESİ
                          </div>
                          <div className="about-certificate-address">
                            GÖLOVASI KÖYÜ SAHİL KESİMİ YUMURTALIK/ADANA/TÜRKİYE
                          </div>
                          <strong className="about-certificate-standard">{item.standard}</strong>
                          <div className="about-certificate-scope">
                            <span>Kapsam/Scope</span>
                            LİMAN YÖNETİM HİZMETLERİ
                            <br />
                            PORT MANAGEMENT SERVICES
                          </div>
                          <div className="about-certificate-lines">
                            <i />
                            <i />
                            <i />
                            <i />
                          </div>
                          <div className="about-certificate-foot">
                            <span className="about-certificate-iaf">IAF</span>
                            <span className="about-certificate-sign" />
                            <span className="about-certificate-qr" />
                          </div>
                        </div>
                      </div>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
